import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUserOrders } from './useUserOrders';
import { useUserOrdersFilters } from './useUserOrdersFilters';
import { useUserOrderActions } from './useUserOrderActions';
import { useFavorites } from '../utils/useFavorites';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { Order } from '../../types/order';
import { FavoriteOrder } from '../../services/favoriteService';

export const useUserOrdersLayout = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { addToCart } = useCart();
  const userInfo = JSON.parse(localStorage.getItem('userInfo') || 'null');
  const [ordersInitialized, setOrdersInitialized] = useState(false);

  // Core data hooks
  const { orders, loading, error, fetchUserOrders } = useUserOrders(userInfo?.id || null);

  const {
    filteredOrders,
    statusFilter,
    setStatusFilter,
    searchTerm,
    setSearchTerm,
    dateFilter,
    setDateFilter,
    getStatusCount,
  } = useUserOrdersFilters(orders);

  const {
    favorites: favoriteOrders,
    loading: favoritesLoading,
    error: favoritesError,
    fetchFavorites,
    removeFavorite: originalRemoveFavorite,
    addFavoriteToList,
    removeFavoriteFromList,
    toggleFavoriteInList,
    favoritesCount,
    hasInitiallyLoaded,
  } = useFavorites(userInfo?.id);

  // Modal state
  const [selectedOrder, setSelectedOrder] = useState<Order | null>(null);
  const [selectedFavorite, setSelectedFavorite] = useState<FavoriteOrder | null>(null);

  // Favorite change handler with instant updates
  const handleFavoriteChange = useCallback(
    (action: 'add' | 'remove', orderId: number, orderDetails?: Order) => {
      if (action === 'add' && orderDetails) {
        addFavoriteToList(orderDetails);
      } else if (action === 'remove') {
        removeFavoriteFromList(orderId);
      }
    },
    [addFavoriteToList, removeFavoriteFromList]
  );

  const {
    favorites: favoriteIds,
    handleReorder,
    toggleFavorite,
    updateFavoriteState,
  } = useUserOrderActions({ onFavoriteChange: handleFavoriteChange });

  // Enhanced handlers
  const handleRemoveFavorite = useCallback(
    async (orderId: number) => {
      await originalRemoveFavorite(orderId);
      updateFavoriteState(orderId, false);
    },
    [originalRemoveFavorite, updateFavoriteState]
  );

  const handleToggleFavoriteFromList = useCallback(
    async (orderId: number) => {
      await toggleFavoriteInList(orderId);
      updateFavoriteState(orderId, false);
    },
    [toggleFavoriteInList, updateFavoriteState]
  );

  const handleFavoriteReorder = useCallback(
    (favorite: FavoriteOrder) => {
      if (favorite.items && favorite.items.length > 0) {
        favorite.items.forEach(item => {
          addToCart(
            {
              id: item.product_id,
              name: item.name,
              price: item.price,
              options: {
                size: (item.size as 'normal' | 'double') || 'normal',
                strength: (item.strength as 'light' | 'normal' | 'strong') || 'normal',
              },
            },
            item.quantity
          );
        });
      }
    },
    [addToCart]
  );

  // Navigation handlers
  const handleGoHome = useCallback(() => {
    navigate('/');
  }, [navigate]);

  // Authentication check
  useEffect(() => {
    if (!isAuthenticated || !userInfo) {
      navigate('/');
      return;
    }
  }, [isAuthenticated, userInfo, navigate]);

  // Reset flags when user changes
  useEffect(() => {
    setOrdersInitialized(false);
  }, [userInfo?.id]);

  // Load orders (sadece ilk kez)
  useEffect(() => {
    if (userInfo?.id && !ordersInitialized && !loading) {
      fetchUserOrders();
      setOrdersInitialized(true);
    }
  }, [userInfo?.id, ordersInitialized, loading, fetchUserOrders]);

  // Load favorites when switching to favorites tab
  useEffect(() => {
    if (statusFilter === 'favorites' && !hasInitiallyLoaded && !favoritesLoading) {
      fetchFavorites();
    }
  }, [statusFilter, hasInitiallyLoaded, favoritesLoading, fetchFavorites]);

  // Computed values
  const hasFilters = statusFilter !== 'all' || !!searchTerm || dateFilter !== 'all';

  const getEmptyStateType = (): 'error' | 'no-orders' | 'no-filtered-results' | 'no-favorites' => {
    if (error) {
      return 'error';
    }
    if (hasFilters && filteredOrders.length === 0) {
      return 'no-filtered-results';
    }
    return 'no-orders';
  };

  const getFavoritesEmptyStateType = ():
    | 'error'
    | 'no-orders'
    | 'no-filtered-results'
    | 'no-favorites' => {
    if (favoritesError) {
      return 'error';
    }
    return 'no-favorites';
  };

  // Modal handlers
  const handleOrderDetailClick = useCallback((order: Order) => {
    setSelectedOrder(order);
  }, []);

  const handleFavoriteDetailClick = useCallback((favorite: FavoriteOrder) => {
    setSelectedFavorite(favorite);
  }, []);

  const handleCloseOrderModal = useCallback(() => {
    setSelectedOrder(null);
  }, []);

  const handleCloseFavoriteModal = useCallback(() => {
    setSelectedFavorite(null);
  }, []);

  const handleFavoriteModalReorder = useCallback(
    (order: Order) => {
      const favorite = favoriteOrders.find(f => f.order_id === order.id);
      if (favorite) {
        handleFavoriteReorder(favorite);
      }
    },
    [favoriteOrders, handleFavoriteReorder]
  );

  return {
    // Authentication & User
    isAuthenticated,
    userInfo,

    // Data
    orders,
    filteredOrders,
    favoriteOrders,
    favoriteIds,

    // Loading states
    loading,
    favoritesLoading,

    // Errors
    error,
    favoritesError,

    // Filters
    statusFilter,
    searchTerm,
    dateFilter,
    hasFilters,

    // Counts
    favoritesCount,
    getStatusCount,

    // Modal state
    selectedOrder,
    selectedFavorite,

    // Computed
    getEmptyStateType,
    getFavoritesEmptyStateType,

    // Handlers - Navigation
    handleGoHome,

    // Handlers - Filters
    setStatusFilter,
    setSearchTerm,
    setDateFilter,

    // Handlers - Orders
    handleOrderDetailClick,
    handleReorder,
    toggleFavorite,

    // Handlers - Favorites
    handleRemoveFavorite,
    handleToggleFavoriteFromList,
    handleFavoriteReorder,
    handleFavoriteDetailClick,

    // Handlers - Modals
    handleCloseOrderModal,
    handleCloseFavoriteModal,
    handleFavoriteModalReorder,

    // Retry handlers
    fetchUserOrders,
    fetchFavorites,
  };
};
